<template>
  <div class="portal-home">
    <Menu class="portal-shell-menu" />
    <div class="portal-home__container">
      <div class="portal-home__hero">
        <AccountBalanceSection
          :account="portalAccount"
          :loading="portalAnalyticsLoading"
        />
        <RewardsCatalogBanner />
        <aside
          v-if="portalTeasersLoading || portalTeasers.length"
          class="portal-home__quick-links"
        >
          <div
            v-if="portalTeasersLoading"
            class="portal-home__quick-links-loading"
          >
            <Loader :show="true" />
          </div>
          <PortalTeaserBox
            v-for="teaser in portalTeasers"
            v-else
            :key="teaser.id"
            :teaser="teaser"
          />
        </aside>
      </div>
      <div class="portal-home__bonus-row">
        <BonusUhidWidget :data="portalBonusUhid" />
        <BonusAwansWidget :data="portalBonusAwans" />
      </div>
      <PromotionsGrid
        :promotions="filteredPromotions"
        :signing-in-promotion-id="signingInPromotionId"
        show-filters
        :active-filter="activeFilter"
        :show-view-toggle="false"
        @update:active-filter="activeFilter = $event"
        @sign-in="handlePromotionSignIn"
      />
      <PortalAnalyticsSection
        :analytics="portalAnalyticsCharts"
        :loading="portalAnalyticsLoading"
        :surcharge-columns="surchargeColumns"
        :surcharge-rows="surchargeRows"
        :surcharge-loading="surchargeLoading"
      />
      <PortalAppsSection />
    </div>
    <PortalFooter
      :links="portalFooterLinks"
      :socials="portalFooterSocials"
      :logo-src="portalFooterLogoSrc"
    />
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import Menu from "@/components/Layout/Menu.vue";
import AccountBalanceSection from "@/components/PortalHome/AccountBalanceSection.vue";
import RewardsCatalogBanner from "@/components/PortalHome/RewardsCatalogBanner.vue";
import PortalTeaserBox from "@/components/PortalHome/PortalTeaserBox.vue";
import Loader from "@/components/General/Loader/index.vue";
import BonusUhidWidget from "@/components/PortalHome/BonusUhidWidget.vue";
import BonusAwansWidget from "@/components/PortalHome/BonusAwansWidget.vue";
import PromotionsGrid from "@/components/PortalHome/PromotionsGrid.vue";
import PortalAnalyticsSection from "@/components/PortalHome/Analytics/PortalAnalyticsSection.vue";
import PortalAppsSection from "@/components/PortalHome/PortalAppsSection.vue";
import PortalFooter from "@/components/PortalHome/PortalFooter.vue";
import { usePortalPromotions } from "@/composables/usePortalPromotions";
import { usePortalAnalytics } from "@/composables/usePortalAnalytics";
import { usePortalSurchargeTable } from "@/composables/usePortalSurchargeTable";
import { usePortalMainBoxes } from "@/composables/usePortalMainBoxes";
import {
  portalBonusUhid,
  portalBonusAwans,
  portalFooterLinks,
  portalFooterSocials,
  portalFooterLogoSrc,
} from "@/data/portal_homepage.js";

const {
  filteredPromotions,
  activeFilter,
  signingInPromotionId,
  handlePromotionSignIn,
} = usePortalPromotions();

const {
  account: portalAccount,
  analyticsCharts: portalAnalyticsCharts,
  loading: portalAnalyticsLoading,
  loadPortalAnalytics,
} = usePortalAnalytics();

const { teasers: portalTeasers, loading: portalTeasersLoading } =
  usePortalMainBoxes();

const {
  columns: surchargeColumns,
  rows: surchargeRows,
  loading: surchargeLoading,
  loadSurchargeTable,
} = usePortalSurchargeTable();

onMounted(() => {
  loadPortalAnalytics();
  loadSurchargeTable();
});
</script>

<style lang="scss">
@import "@/styles/portal-home/portal-shell-menu-gap";
@import "@/styles/portal-home/bonus-progress-widget-shared";
</style>

<style scoped lang="scss">
@import "@/styles/portal-home/portal-home-page";
</style>
